/**
 * Jurisdictions Admin — list page (SystemAdmin only).
 * Browse all jurisdictions with a snapshot of retention + capability flags. Click
 * a row to land on the edit page where the retention policy can be tuned.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Map, Loader2, RefreshCw, AlertCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import Navigation from '../../components/Navigation';
import Sidebar from '../../components/Sidebar';
import Footer from '../../components/Footer';
import { getErrorMessage } from '../../services/apiService';
import { listJurisdictions, JurisdictionAdminResponse } from '../../services/jurisdictionsAdminService';

const Jurisdictions: React.FC = () => {
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const isSystemAdmin = user?.isSystemAdmin === true;

  useEffect(() => {
    if (authLoading) return;
    if (!user) navigate('/login');
    else if (!isSystemAdmin) navigate('/dashboard');
  }, [user, authLoading, navigate, isSystemAdmin]);

  const [rows, setRows] = useState<JurisdictionAdminResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [filter, setFilter] = useState('');

  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');
      setRows(await listJurisdictions());
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { if (isSystemAdmin) load(); }, [isSystemAdmin, load]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#f8fafc] to-[#e2e8f0]">
        <Loader2 className="w-8 h-8 animate-spin text-[#c41e3a]" />
      </div>
    );
  }

  const filtered = rows.filter(r =>
    !filter || r.code.includes(filter.toUpperCase()) || r.name.toLowerCase().includes(filter.toLowerCase()),
  );

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-[#f8fafc] to-[#e2e8f0] text-[#0f172a]">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-radial from-[#c41e3a]/[0.03] to-transparent rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-radial from-[#c41e3a]/[0.04] to-transparent rounded-full blur-3xl"></div>
      </div>

      <Navigation onMenuClick={() => setIsSidebarOpen(true)} />
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <main className="flex-1 lg:ml-[220px] pt-28 md:pt-32 px-4 lg:px-8 py-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl font-bold text-[#0f172a] flex items-center gap-3">
                <Map className="w-7 h-7 text-[#c41e3a]" />
                Jurisdictions
              </h1>
              <p className="text-[#64748b] mt-1">Per-state retention policy &amp; capability flags. Click a row to edit.</p>
            </div>
            <div className="flex items-center gap-3">
              <input
                type="text"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Filter by code or name…"
                className="px-3 py-2 rounded-lg border border-black/[0.08] bg-white text-sm focus:outline-none focus:border-[#c41e3a]/50 w-56"
              />
              <button
                onClick={load}
                disabled={isLoading}
                className="flex items-center gap-2 px-4 py-2 rounded-lg border border-black/[0.08] bg-white text-[#475569] hover:border-[#c41e3a]/50 transition-all"
              >
                <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 rounded-xl border flex items-start space-x-3 bg-red-50 border-red-200">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <div className="bg-white rounded-2xl border border-black/[0.08] overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#f8fafc] text-[#475569] text-xs uppercase tracking-wider">
                  <tr>
                    <th className="px-4 py-3 text-left">Code</th>
                    <th className="px-4 py-3 text-left">Name</th>
                    <th className="px-4 py-3 text-left">Type</th>
                    <th className="px-4 py-3 text-center">Active</th>
                    {/* NMVTIS column hidden for now (per request) — restore by uncommenting this and the matching cell below. */}
                    {/* <th className="px-4 py-3 text-center">NMVTIS</th> */}
                    <th className="px-4 py-3 text-center">DMV Connected</th>
                    <th className="px-4 py-3 text-center" title="Whether clients may use this state. System admins always see it.">Client MVR</th>
                    <th className="px-4 py-3 text-right">Retention (days)</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr><td colSpan={7} className="text-center py-12 text-[#64748b]"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading…</td></tr>
                  ) : filtered.length === 0 ? (
                    <tr><td colSpan={7} className="text-center py-12 text-[#64748b]">No jurisdictions match.</td></tr>
                  ) : (
                    filtered.map((j) => (
                      <tr
                        key={j.id}
                        onClick={() => navigate(`/admin/jurisdictions/${j.code}`)}
                        className="border-t border-black/[0.05] hover:bg-[#fafbfc] cursor-pointer transition-colors"
                      >
                        <td className="px-4 py-3 font-mono text-[#0f172a]">{j.code}</td>
                        <td className="px-4 py-3 text-[#0f172a]">{j.name}</td>
                        <td className="px-4 py-3 text-[#64748b]">{j.type}</td>
                        <td className="px-4 py-3 text-center">{j.isActive ? '✓' : '—'}</td>
                        {/* NMVTIS column hidden for now (per request) */}
                        {/* <td className="px-4 py-3 text-center">{j.nmvtisCovered ? '✓' : '—'}</td> */}
                        <td className="px-4 py-3 text-center">{j.dmvConnected ? '✓' : '—'}</td>
                        <td className="px-4 py-3 text-center">{j.mvrEnabledByDefault === false ? <span className="text-red-500" title="Connected, but withdrawn from clients">blocked</span> : '✓'}</td>
                        <td className="px-4 py-3 text-right tabular-nums font-medium text-[#0f172a]">{j.dataRetentionDays ?? <span className="text-[#64748b] font-normal">Global</span>}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Jurisdictions;
